import { Divider, Grid, Typography } from "@mui/material";
import MessageStore from "components/ManagerInteractions/Stores/MessageStore";
import AcxMainTextField from "components/UI/AcxMainTextField";
import AcxButton from "components/UI/AcxButton";
import AcxToggleButton from "components/UI/AcxToggleButton";
import VerticalDrawerContentTemplate from "components/UI/Drawer/VerticalDrawerContentTemplate";
import theme from "Theme/AppTheme";
import { observer } from "mobx-react";
import React, {
    useCallback,
    useEffect,
    useLayoutEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import { useStore } from "utils/useStore";
import { ApplicationFiltersStore } from "stores/ApplicationFilters/ApplicationFiltersStore";
import { AuthStore } from "stores/AuthStore";
import { LayoutDrawerStore } from "stores/Layout/LayoutDrawerStore";
import { SignalsService } from "services/SignalsService";
import {
    getDefaultSwapGroupingAndX,
    SignalsObjectType,
    type SignalsParsedVizOptions,
} from "../../SignalsModels";
import {
    readGroupByDraft,
    writeGroupByDraft,
} from "../../Grouping/groupByDraft";
import SignalsPageStore from "../../Store/SignalsPageStore";
import { useCurrentDashboard } from "../../useCurrentDashboard";
import { canReorderChart } from "../_DataEndpointConfig/Reorder";
import ChartCardStore from "../ChartCard/ChartCardStore";
import { ObjectFiltersSection } from "../ObjectFilters";
import SmallCardStore from "../SmallCard/SmallCardStore";
import TableCardStore from "../TableCard/TableCardStore";
import UniqueCardStore from "../UniqueCard/UniqueCardStore";
import GroupByConfigurationSection from "./GroupBy";
import ReorderConfigurationSection from "./ReorderConfigurationSection";
import { findStandardReportTitle } from "./standardReportTitle";

const signalsService = new SignalsService();

type ConfigurableStore =
    | ChartCardStore
    | TableCardStore
    | UniqueCardStore<any>
    | SmallCardStore;

type Props = {
    store: ConfigurableStore;
};

const supportsTitleByObjectType = (store: ConfigurableStore) =>
    store.objectType === SignalsObjectType.Chart ||
    store.objectType === SignalsObjectType.Unique ||
    store.objectType === SignalsObjectType.Table ||
    store.objectType === SignalsObjectType.SmallCard;

const supportsSubtitleByObjectType = (store: ConfigurableStore) =>
    store.objectType === SignalsObjectType.Chart ||
    store.objectType === SignalsObjectType.Unique ||
    store.objectType === SignalsObjectType.Table;

const supportsGroupingByObjectType = (store: ConfigurableStore) =>
    store.objectType === SignalsObjectType.Chart;

const supportsObjectFilteringByObjectType = (store: ConfigurableStore) =>
    store.objectType === SignalsObjectType.Chart ||
    store.objectType === SignalsObjectType.Unique ||
    store.objectType === SignalsObjectType.Table ||
    store.objectType === SignalsObjectType.SmallCard;

type ScaleToggleValue = "linear" | "log";

export const ObjectConfigurationDrawer: React.FC<Props> = observer(({ store }) => {
    const drawerHorizontalPadding = 3;
    const currentDashboard = useCurrentDashboard();
    const messageStore = useStore(MessageStore);
    const applicationFiltersStore = useStore(ApplicationFiltersStore);
    const drawerStore = useStore(LayoutDrawerStore);
    const authStore = useStore(AuthStore);
    const signalsPageStore = useStore(SignalsPageStore);
    const savedRef = useRef(false);
    const wasRefreshingRef = useRef(store.isRefreshing);

    const originalReportName = findStandardReportTitle(
        signalsPageStore.standardDashboards,
        store.dataEndpoint,
    );

    const chartStore =
        store.objectType === SignalsObjectType.Chart
            ? (store as ChartCardStore)
            : null;

    // Generic reorder of the chart's x-axis order-rule values (e.g. the "Models" bar order on a grouped
    // model-results chart). Opt-in via the endpoint config's `reorder`; visibility rules (including the
    // audience policy) live in canReorderChart so they stay centralized.
    const reorderableValues =
        chartStore && Array.isArray(chartStore.orderRule?.orderByValues)
            ? (chartStore.orderRule!.orderByValues as string[])
            : null;
    const showReorder = canReorderChart(chartStore, {
        isInternalUser: authStore.isAuthcxUser(),
    });

    const isEditableAndSaveable = store.isEditable && store.isSaveable;
    // We enable the configure menu even when you can't edit/persist changes on the chart,
    // so we prevent changing the title/subtitle by checking it here.
    const showTitle = supportsTitleByObjectType(store) && isEditableAndSaveable;
    const showSubtitle =
        supportsSubtitleByObjectType(store) && isEditableAndSaveable;
    const showScaleType = !!chartStore && chartStore.canChangeScaleType;
    const groupingSupported =
        supportsGroupingByObjectType(store) &&
        !!chartStore &&
        chartStore.config.grouping.enabled;
    const showGrouping =
        groupingSupported &&
        // Grouping UI only renders when backend catalog data is loaded.
        (chartStore?.optionsStore.signalsGroupByCatalog.length ?? 0) > 0;
    const groupingDisabledReason =
        showGrouping && chartStore?.comparePrevEnabled
            ? "Disable compare previous to enable grouping."
            : undefined;
    const showObjectFilters =
        supportsObjectFilteringByObjectType(store) &&
        store.config.capabilities.objectFilters &&
        !!store.objectFiltersStore &&
        store.isEditable;
    const showTemporaryConfigWarning =
        !isEditableAndSaveable &&
        (showScaleType || showGrouping || showObjectFilters);
    const objectLastAppliedSourceFilters =
        store.lastAppliedSourceFiltersForObjectFiltering;
    const objectFilterSourceStore = useMemo(() => {
        const sourceFilters =
            objectLastAppliedSourceFilters ?? currentDashboard?.lastAppliedFilters;

        if (!sourceFilters) {
            return applicationFiltersStore;
        }

        // Prefer the source filters this card most recently rendered against over the
        // current top-level filter bar so the drawer explains restrictions relative to
        // the data the user is actually looking at.
        const sourceStore = applicationFiltersStore.toInstance();
        sourceStore.applyExternalFilters(sourceFilters);
        return sourceStore;
    }, [
        applicationFiltersStore,
        currentDashboard?.lastAppliedFilters,
        objectLastAppliedSourceFilters,
    ]);

    const [localTitle, setLocalTitle] = useState(store.title ?? "");
    const [localSubtitle, setLocalSubtitle] = useState(store.subtitle ?? "");
    const [localVizOptions, setLocalVizOptions] = useState<SignalsParsedVizOptions>(
        chartStore?.parsedVizOptions ?? {},
    );
    const [isSaving, setIsSaving] = useState(false);
    const [localReorderValues, setLocalReorderValues] = useState<string[]>(
        reorderableValues ? [...reorderableValues] : [],
    );
    // The order-rule values as they were when the drawer opened, used to detect changes and to revert
    // the optimistic preview on Cancel / failed save.
    const reorderSnapshotRef = useRef<string[] | null>(null);
    const closeDrawer = useCallback(() => {
        drawerStore.closeAndResetDrawer();
    }, [drawerStore]);

    useEffect(() => {
        setLocalTitle(store.title ?? "");
        setLocalSubtitle(store.subtitle ?? "");
        setLocalVizOptions(chartStore?.parsedVizOptions ?? {});
    }, [
        chartStore?.parsedVizOptions,
        store.id,
        store.subtitle,
        store.title,
    ]);

    useLayoutEffect(() => {
        if (!showObjectFilters) {
            return;
        }

        savedRef.current = false;
        store.discardObjectFilterDraft();

        return () => {
            if (!savedRef.current) {
                store.discardObjectFilterDraft();
            }
        };
    }, [showObjectFilters, store, store.id]);

    // Capture the original order on open; revert the optimistic preview on close-without-save.
    useLayoutEffect(() => {
        if (!showReorder || !chartStore) return;

        const original = Array.isArray(chartStore.orderRule?.orderByValues)
            ? [...(chartStore.orderRule!.orderByValues as string[])]
            : [];
        savedRef.current = false;
        reorderSnapshotRef.current = original;
        setLocalReorderValues(original);

        return () => {
            if (savedRef.current) return;
            const snapshot = reorderSnapshotRef.current;
            if (chartStore.orderRule && snapshot) {
                chartStore.orderRule = {
                    ...chartStore.orderRule,
                    orderByValues: snapshot,
                };
                chartStore.reapplyTransformsToCurrentData();
            }
        };
    }, [showReorder, store.id, chartStore]);

    // Optimistically preview the dragged order on the live chart. Bar ordering is baked into the chart's
    // data at fetch time (the order rule is a client-side transform), so we update the order rule ref and
    // re-run the transforms over the cached response — re-sorting the already-fetched data, no refetch.
    useEffect(() => {
        if (!showReorder || !chartStore?.orderRule) return;

        const current = chartStore.orderRule.orderByValues;
        const unchanged =
            Array.isArray(current) &&
            current.length === localReorderValues.length &&
            current.every((value, index) => value === localReorderValues[index]);
        if (unchanged) return;

        chartStore.orderRule = {
            ...chartStore.orderRule,
            orderByValues: localReorderValues,
        };
        chartStore.reapplyTransformsToCurrentData();
    }, [showReorder, chartStore, localReorderValues]);

    useEffect(() => {
        const wasRefreshing = wasRefreshingRef.current;
        wasRefreshingRef.current = store.isRefreshing;

        if (!wasRefreshing && store.isRefreshing && !isSaving) {
            closeDrawer();
        }
    }, [closeDrawer, isSaving, store.isRefreshing]);


    const fullBleedDividerSx = {
        ml: theme.spacing(-drawerHorizontalPadding),
        mr: theme.spacing(-drawerHorizontalPadding),
        width: `calc(100% + ${theme.spacing(drawerHorizontalPadding * 2)})`,
    };

    const [disableSave, setDisableSave] = useState(false);

    const handleSave = async () => {
        if (disableSave) return;

        if (!currentDashboard) {
            messageStore.logError("Unable to update card: dashboard not found.");
            return;
        }

        // Persist grouping when the endpoint supports it. The controls may still be hidden while
        // catalog data loads, so support is checked separately from visibility.
        const grouping = groupingSupported
            ? readGroupByDraft(localVizOptions)
            : undefined;
        const isTemporalChart = !!chartStore?.config.renderOptions.isTemporal;
        const isDonutChart = chartStore?.chartType === "Donut";
        const swapGroupingAndX = !grouping
            ? undefined
            : isTemporalChart
              ? false
              : isDonutChart
                ? true
                : localVizOptions.swapGroupingAndX ??
                  getDefaultSwapGroupingAndX({
                      groupByOption: grouping.option,
                      isTemporal: store.config.renderOptions.isTemporal,
                      endpoint: store.dataEndpoint,
                  });
        const normalizedVizOptions: SignalsParsedVizOptions = {
            ...writeGroupByDraft(localVizOptions, grouping),
            scaleType: showScaleType ? localVizOptions.scaleType : undefined,
            swapGroupingAndX,
        };

        const currentVizOptionsString = JSON.stringify(
            chartStore?.parsedVizOptions ?? {},
        );
        const nextVizOptionsString = JSON.stringify(normalizedVizOptions ?? {});
        const vizOptionsChanged =
            !!chartStore && currentVizOptionsString !== nextVizOptionsString;

        const objectFiltersChanged =
            showObjectFilters && store.objectFiltersStore
                ? store.objectFiltersChangedSinceCommit()
                : false;
        const nextObjectFiltersString =
            showObjectFilters && store.objectFiltersStore
                ? store.getSerializedObjectFiltersForPersistence()
                : undefined;

        if (showObjectFilters && store.objectFiltersStore) {
            const valid =
                await store.objectFiltersStore.validateWordsAndPhrases();
            if (!valid)
                return messageStore.logError(
                    "Invalid Words and Phrases Syntax, please correct the following before continuing: \n" +
                        store.objectFiltersStore.wordsAndPhrasesStore
                            .wordsPhrasesFtsSyntaxError,
                );
        }

        setIsSaving(true);

        let updateSucceeded = true;

        if (store.objectType === SignalsObjectType.Chart && chartStore) {
            updateSucceeded = await chartStore.updateChartConfiguration({
                dashboardId: currentDashboard.id,
                title: localTitle,
                subtitle: localSubtitle,
                vizOptions: normalizedVizOptions,
                objectFilters: objectFiltersChanged
                    ? nextObjectFiltersString
                    : undefined,
            });

            if (updateSucceeded) {
                currentDashboard.signalsObjects = currentDashboard.signalsObjects.map(
                    (obj) =>
                        obj.id === chartStore.id
                            ? {
                                  ...obj,
                                  title: localTitle,
                                  subtitle: localSubtitle,
                                  vizOptions: nextVizOptionsString,
                                  objectFilters: objectFiltersChanged
                                      ? nextObjectFiltersString ??
                                        obj.objectFilters
                                      : obj.objectFilters,
                              }
                            : obj,
                );
            }
        }

        if (store.objectType === SignalsObjectType.Table) {
            const tableStore = store as TableCardStore;
            updateSucceeded = await tableStore.updateTableConfiguration({
                title: localTitle,
                subtitle: localSubtitle,
                objectFilters: objectFiltersChanged
                    ? nextObjectFiltersString
                    : undefined,
            });

            if (updateSucceeded) {
                currentDashboard.signalsObjects = currentDashboard.signalsObjects.map(
                    (obj) =>
                        obj.id === tableStore.id
                            ? {
                                  ...obj,
                                  title: localTitle,
                                  subtitle: localSubtitle,
                                  objectFilters: objectFiltersChanged
                                      ? nextObjectFiltersString ??
                                        obj.objectFilters
                                      : obj.objectFilters,
                              }
                            : obj,
                );
            }
        }

        if (store.objectType === SignalsObjectType.Unique) {
            const uniqueStore = store as UniqueCardStore<any>;
            updateSucceeded = await uniqueStore.updateUniqueConfiguration({
                title: localTitle,
                subtitle: localSubtitle,
                objectFilters: objectFiltersChanged
                    ? nextObjectFiltersString
                    : undefined,
            });

            if (updateSucceeded) {
                currentDashboard.signalsObjects = currentDashboard.signalsObjects.map(
                    (obj) =>
                        obj.id === uniqueStore.id
                            ? {
                                  ...obj,
                                  title: localTitle,
                                  subtitle: localSubtitle,
                                  objectFilters: objectFiltersChanged
                                      ? nextObjectFiltersString ??
                                        obj.objectFilters
                                      : obj.objectFilters,
                              }
                            : obj,
                );
            }
        }

        if (store.objectType === SignalsObjectType.SmallCard) {
            const smallCardStore = store as SmallCardStore;
            updateSucceeded = await smallCardStore.updateSmallCardConfiguration({
                title: localTitle,
                objectFilters: objectFiltersChanged
                    ? nextObjectFiltersString
                    : undefined,
            });

            if (updateSucceeded) {
                currentDashboard.signalsObjects = currentDashboard.signalsObjects.map(
                    (obj) =>
                        obj.id === smallCardStore.id
                            ? {
                                  ...obj,
                                  objectFilters: objectFiltersChanged
                                      ? nextObjectFiltersString ??
                                        obj.objectFilters
                                      : obj.objectFilters,
                                  title: localTitle,
                              }
                            : obj,
                );
            }
        }

        if (!updateSucceeded) {
            setIsSaving(false);
            return;
        }

        if (objectFiltersChanged) {
            store.commitObjectFilters();
        }

        if (showReorder && chartStore?.orderRuleId) {
            const snapshot = reorderSnapshotRef.current ?? [];
            const reorderChanged =
                snapshot.length !== localReorderValues.length ||
                snapshot.some((value, index) => value !== localReorderValues[index]);

            if (reorderChanged) {
                try {
                    await signalsService.updateOrderRuleValues(
                        chartStore.orderRuleId,
                        localReorderValues,
                    );
                    // Keep the optimistic order; sync the dashboard cache so a future open reflects it.
                    currentDashboard.signalsObjects =
                        currentDashboard.signalsObjects.map((obj) =>
                            obj.id === chartStore.id && obj.orderRule
                                ? {
                                      ...obj,
                                      orderRule: {
                                          ...obj.orderRule,
                                          orderByValues: localReorderValues,
                                      },
                                  }
                                : obj,
                        );
                } catch (_error) {
                    // Revert the optimistic preview to the order as it was when the drawer opened.
                    if (chartStore.orderRule) {
                        chartStore.orderRule = {
                            ...chartStore.orderRule,
                            orderByValues: snapshot,
                        };
                        chartStore.reapplyTransformsToCurrentData();
                    }
                    messageStore.logError("Failed to update order.");
                    setIsSaving(false);
                    return;
                }
            }
        }

        if (
            (store.objectType === SignalsObjectType.Chart &&
                (vizOptionsChanged || objectFiltersChanged)) ||
            ((store.objectType === SignalsObjectType.Table ||
                store.objectType === SignalsObjectType.Unique ||
                store.objectType === SignalsObjectType.SmallCard) &&
                objectFiltersChanged)
        ) {
            await store.runRefresh(true);
        }

        savedRef.current = true;
        if (store.objectType === SignalsObjectType.Chart) {
            messageStore.logMessage("Chart updated", "success");
        } else {
            messageStore.logMessage("Card updated", "success");
        }
        setIsSaving(false);
        closeDrawer();
    };

    return (
        <VerticalDrawerContentTemplate
            width="35vw"
            title={
                <>
                    <Typography
                        component="div"
                        noWrap
                        sx={{
                            color: theme.palette.gray[700],
                            fontSize: "24px",
                            fontWeight: 600,
                            lineHeight: "32px",
                        }}
                    >
                        Configure
                    </Typography>
                    {originalReportName && (
                        <Typography
                            component="div"
                            data-testid="configure-original-report-name"
                            sx={{
                                marginTop: theme.spacing(0.5),
                                color: theme.palette.gray[700],
                                fontSize: "13px",
                                fontWeight: 600,
                                overflowWrap: "anywhere",
                            }}
                        >
                            {originalReportName}
                        </Typography>
                    )}
                </>
            }
            titleStyles={{
                padding: theme.spacing(2),
            }}
            contentStyles={{
                paddingTop: theme.spacing(2),
                paddingBottom: theme.spacing(2),
                paddingLeft: theme.spacing(drawerHorizontalPadding),
                paddingRight: theme.spacing(drawerHorizontalPadding),
                height: "100%",
                overflowY: "auto",
            }}
            content={
                <Grid container direction="column" rowGap={2}>
                    {showTitle && (
                        <>
                            <Grid item xs={12}>
                                <AcxMainTextField
                                    id={`${store.id}-config-title`}
                                    labelText="Title"
                                    value={localTitle}
                                    onChange={(e) => setLocalTitle(e.target.value)}
                                    fullWidth
                                />
                            </Grid>
                            {showSubtitle && (
                                <Grid item xs={12}>
                                    <AcxMainTextField
                                        id={`${store.id}-config-subtitle`}
                                        labelText="Sub-title"
                                        value={localSubtitle}
                                        onChange={(e) => setLocalSubtitle(e.target.value)}
                                        fullWidth
                                        multiline
                                        multilinePadding
                                        rowsMax={3}
                                    />
                                </Grid>
                                )}
                            <Grid item xs={12} sx={fullBleedDividerSx}>
                                <Divider />
                            </Grid>
                        </>
                    )}

                    {showScaleType && (
                        <>
                            <Grid item xs={12}>
                                <AcxToggleButton
                                    id={`${store.id}-config-scale-type`}
                                    inputLabel="Scale"
                                    items={[
                                        {
                                            id: `${store.id}-config-scale-linear`,
                                            value: "linear",
                                            element: "Linear",
                                        },
                                        {
                                            id: `${store.id}-config-scale-log`,
                                            value: "log",
                                            element: "Log",
                                        },
                                    ]}
                                    exclusive
                                    defaultValue={
                                        localVizOptions.scaleType
                                            ? "log"
                                            : "linear"
                                    }
                                    onChange={(value) => {
                                        if (
                                            value !== "linear" &&
                                            value !== "log"
                                        ) {
                                            return;
                                        }

                                        const scaleValue: ScaleToggleValue = value;
                                        setLocalVizOptions((previous) => ({
                                            ...previous,
                                            scaleType:
                                                scaleValue === "log"
                                                    ? "log"
                                                    : undefined,
                                        }));
                                    }}
                                    style={{
                                        width: "100%",
                                    }}
                                    sx={{
                                        width: "100%",
                                        "& .MuiToggleButton-root": {
                                            flex: 1,
                                            minWidth: 0,
                                        },
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12} sx={fullBleedDividerSx}>
                                <Divider />
                            </Grid>
                        </>
                    )}

                    {showReorder && chartStore && (
                        <>
                            <Grid item xs={12}>
                                <ReorderConfigurationSection
                                    chartStore={chartStore}
                                    localOrder={localReorderValues}
                                    setLocalOrder={setLocalReorderValues}
                                />
                            </Grid>
                            <Grid item xs={12} sx={fullBleedDividerSx}>
                                <Divider />
                            </Grid>
                        </>
                    )}

                    {showGrouping && (
                        <>
                            <Grid item xs={12}>
                                <GroupByConfigurationSection
                                    chartStore={chartStore}
                                    localVizOptions={localVizOptions}
                                    setLocalVizOptions={setLocalVizOptions}
                                    disabledReason={groupingDisabledReason}
                                    setDisableSave={setDisableSave}
                                />
                            </Grid>
                            <Grid item xs={12} sx={fullBleedDividerSx}>
                                <Divider />
                            </Grid>
                        </>
                    )}
                    {showObjectFilters && (
                        <>
                            <Grid item xs={12} sx={{ pt: 0.5 }}>
                                <ObjectFiltersSection
                                    store={store}
                                    sourceFiltersStore={objectFilterSourceStore}
                                />
                            </Grid>
                        </>
                    )}
                    {showTemporaryConfigWarning && (
                        <Grid item xs={12}>
                            <Typography
                                variant="body2"
                                color={theme.palette.error.main}
                                sx={{
                                    whiteSpace: "normal",
                                    overflowWrap: "anywhere",
                                }}
                            >
                                You can configure this report, but your selections won't persist when you refresh or log out. If you want to save this view, pin the chart to a custom dashboard.
                            </Typography>
                        </Grid>
                    )}
                </Grid>
            }
            footerButtons={
                <Grid container justifyContent="flex-end" columnSpacing={1}>
                    <Grid item>
                        <AcxButton
                            id={`${store.id}-config-cancel`}
                            color="white"
                            variant="outlined"
                            onClick={closeDrawer}
                            disabled={isSaving}
                        >
                            Cancel
                        </AcxButton>
                    </Grid>
                    <Grid item>
                        <AcxButton
                            id={`${store.id}-config-save`}
                            color="primary"
                            onClick={handleSave}
                            disabled={
                                disableSave ||
                                isSaving ||
                                !!store.objectFiltersStore?.hasError ||
                                !!store.objectFiltersStore?.wordsAndPhrasesStore
                                    .hasError
                            }
                            loading={isSaving}
                        >
                            {store.isEditable && store.isSaveable ? "Save" : "Apply"}
                        </AcxButton>
                    </Grid>
                </Grid>
            }
        />
    );
});

export default ObjectConfigurationDrawer;
